import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate } from '../api/dataverseClient';
import { ActivityLogEntry } from '../types';

// Dataverse entitytype picklist values (verified Apr 19):
export const ActivityEntityType = {
  Task:          100000000,
  Issue:         100000001,
  Risk:          100000002,
  Decision:      100000003,
  ChangeRequest: 100000004,
  Milestone:     100000005,
  Sprint:        100000006,
  Project:       100000007,
  Comment:       100000008,
  AIAgent:       100000009,
} as const;

const ACTIVITY_FIELDS: string[] = [
  'dagrun_pm_activitylogid', 'dagrun_pm_name', 'dagrun_pm_actiontype',
  'dagrun_pm_actiondescription', 'dagrun_pm_actorid', 'dagrun_pm_actorname',
  'dagrun_pm_actorisai',
  'dagrun_pm_entitytype', 'dagrun_pm_entityid', 'dagrun_pm_entitytitle',
  'createdon', '_dagrun_pm_projectid_value',
];

export async function getProjectActivity(
  pca: IPublicClientApplication,
  projectId: string,
  top = 50
): Promise<ActivityLogEntry[]> {
  return dvList<ActivityLogEntry>(pca, 'dagrun_pm_activitylogs', {
    select: ACTIVITY_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'createdon desc',
    top,
  });
}

// Activity scoped to a single entity (Task, Issue, etc.) — used by the
// Task detail drawer's Activity tab. Oldest at top for chronological
// feed reading (spec: "chronological feed, oldest at top").
export async function getEntityActivity(
  pca: IPublicClientApplication,
  entityType: number,
  entityId: string,
  top = 100
): Promise<ActivityLogEntry[]> {
  return dvList<ActivityLogEntry>(pca, 'dagrun_pm_activitylogs', {
    select: ACTIVITY_FIELDS,
    filter: `dagrun_pm_entitytype eq ${entityType} and dagrun_pm_entityid eq '${entityId}'`,
    orderby: 'createdon asc',
    top,
  });
}

export interface LogActivityInput {
  projectId: string;
  entityType: number;
  entityId: string;
  entityTitle?: string;
  action: string;         // short, e.g. "Created", "StatusChanged"
  description?: string;   // human-readable detail, e.g. "Not Started → In Progress"
  actorId?: string;
  actorName?: string;
}

// Fire-and-forget activity log write. Never blocks primary operation.
// Errors are swallowed — activity logging is a nice-to-have side effect.
export async function logActivity(pca: IPublicClientApplication, input: LogActivityInput): Promise<void> {
  try {
    const body: Record<string, unknown> = {
      dagrun_pm_name: input.action,
      dagrun_pm_actiontype: input.action,
      dagrun_pm_actiondescription: input.description ?? '',
      dagrun_pm_entitytype: input.entityType,
      dagrun_pm_entityid: input.entityId,
      dagrun_pm_actorisai: false,
      'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
    };
    if (input.entityTitle) body.dagrun_pm_entitytitle = input.entityTitle;
    if (input.actorId)     body.dagrun_pm_actorid = input.actorId;
    if (input.actorName)   body.dagrun_pm_actorname = input.actorName;
    await dvCreate(pca, 'dagrun_pm_activitylogs', body);
  } catch { /* best effort only */ }
}

export function relativeTime(iso: string): string {
  const dt = new Date(iso).getTime();
  const diff = Date.now() - dt;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
